import React, { useState, useEffect, useRef } from 'react';
import { getProfilePicture } from '../services/profileService';
import { useThemeSettings } from '@/contexts/ThemeContext';
import { User } from 'lucide-react';

const ProfilePicture = ({ 
  userId, 
  userName,
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  className = '',
  showFallback = true 
}) => {
  const [pictureUrl, setPictureUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  
  // Store timeout reference to allow cleanup
  const retryTimeoutRef = useRef(null);

  // Size mappings
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      const url = await getProfilePicture(userId);
      setPictureUrl(url);
      setIsLoading(false);
    };

    fetchProfilePicture();
  }, [userId]);

  // Reset retry count and error state when picture URL changes
  // Also clear any pending retry timeouts
  useEffect(() => {
    setRetryCount(0);
    setImageError(false);
    
    // Clear pending timeout when pictureUrl changes
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [pictureUrl]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  const handleImageError = (e) => {
    const img = e.target;
    const isGoogleImage = pictureUrl && pictureUrl.includes('googleusercontent.com');
    
    // For Google images with 429 rate limit errors, retry with exponential backoff
    if (isGoogleImage && retryCount < 3) {
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
      console.log(`Profile picture rate limited (429), retrying in ${delay}ms (attempt ${retryCount + 1}/3)...`);
      
      // Clear any existing timeout before setting a new one
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      // Store timeout reference for cleanup
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false); // Reset error to trigger retry
        // Force image reload by updating src with cache buster
        if (img) {
          try {
            // Use URL API to properly handle existing query parameters
            const url = new URL(pictureUrl);
            url.searchParams.set('retry', Date.now().toString());
            img.src = url.toString();
          } catch (error) {
            // Fallback for invalid URLs: check if URL already has query params
            const separator = pictureUrl.includes('?') ? '&' : '?';
            img.src = `${pictureUrl}${separator}retry=${Date.now()}`;
          }
        }
        // Clear the ref after timeout completes
        retryTimeoutRef.current = null;
      }, delay);
      return; // Don't set error state yet, allow retry
    }
    
    // Final failure or non-Google image error
    if (img && (img.naturalWidth === 0 || img.naturalHeight === 0)) {
      console.warn('Failed to load profile picture - image could not be loaded');
    } else {
      console.warn('Failed to load profile picture');
    }
    setImageError(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} ${className} rounded-full bg-gray-200 animate-pulse`} />
    );
  }

  // Show picture if available and no error
  if (pictureUrl && !imageError) {
    // Calculate padding for colorful border effect
    const borderPadding = {
      sm: 'p-0.5',
      md: 'p-0.5',
      lg: 'p-1',
      xl: 'p-1.5'
    };

    return (
      <div 
        className={`${sizeClasses[size]} ${borderPadding[size]} ${className} rounded-full flex items-center justify-center`}
        style={{
          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
        }}
      >
        <img
          src={pictureUrl}
          alt="Profile"
          className="w-full h-full rounded-full object-cover"
          onError={handleImageError}
          loading="lazy"
        />
      </div>
    );
  }

  // Fallback: Show standard profile icon (no gradient, no initials — matches "no image" expectation)
  if (showFallback) {
    const iconSizes = {
      sm: 16,
      md: 20,
      lg: 28,
      xl: 40
    };

    return (
      <div
        className={`${sizeClasses[size]} ${className} rounded-full flex items-center justify-center ${isDarkTheme ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'}`}
        aria-hidden="true"
      >
        <User size={iconSizes[size]} className="flex-shrink-0" strokeWidth={2} />
      </div>
    );
  }

  return null;
};

export default ProfilePicture;

